import {
  GradientBorder,
  IconBox,
  VisionButton,
  VisionCard,
  VisionInput,
} from "@/components/VisionUI"
import { Field } from "@/components/ui/field"
import { ReactIcon } from "@/components/ui/icon"
import { InputGroup } from "@/components/ui/input-group"
import { RouterLink } from "@/components/ui/router-link"
import { useSemanticColors } from "@/hooks/useSemanticColors"
import { effects } from "@/theme"
import { Container, Flex, Heading, Image, Text, VStack } from "@chakra-ui/react"
import { createFileRoute, redirect } from "@tanstack/react-router"
import { type SubmitHandler, useForm } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { FaEye, FaEyeSlash, FaUserPlus } from "react-icons/fa"
import { useBoolean } from "usehooks-ts"
import type { UserCreate } from "../client"
import useAuth, { isLoggedIn } from "../hooks/useAuth"
import { confirmPasswordRules, emailPattern, passwordRules } from "../utils"

export const Route = createFileRoute("/signup")({
  component: SignUp,
  beforeLoad: async () => {
    if (isLoggedIn()) {
      throw redirect({
        to: "/",
      })
    }
  },
})

interface UserCreateForm extends UserCreate {
  confirm_password: string
}

function SignUp() {
  const { t } = useTranslation("auth")
  const { value: show, toggle } = useBoolean()
  const { signUpMutation } = useAuth()
  const sc = useSemanticColors()
  const {
    register,
    handleSubmit,
    getValues,
    formState: { errors, isSubmitting },
  } = useForm<UserCreateForm>({
    mode: "onBlur",
    criteriaMode: "all",
    defaultValues: {
      email: "",
      full_name: "",
      password: "",
      confirm_password: "",
    },
  })

  const onSubmit: SubmitHandler<UserCreateForm> = (data) => {
    signUpMutation.mutate(data)
  }

  return (
    <Flex
      minH="100vh"
      align="center"
      justify="center"
      bg={sc.pageBg}
      backgroundImage={effects.gradients.bgGradient}
      backgroundAttachment="fixed"
      p={{ base: 3, md: 4 }}
    >
      <Container maxW="md" px={0}>
        <VStack gap={6}>
          <Image
            src="/qdat-logo.svg"
            alt="QDAT"
            h={{ base: "40px", md: "50px" }}
            mb={2}
          />
          <GradientBorder borderRadius="30px" p={0}>
            <VisionCard
              variant="elevated"
              p={{ base: 5, md: 8 }}
              borderRadius="30px"
            >
              <VStack gap={6} as="form" onSubmit={handleSubmit(onSubmit)}>
                <Flex direction="column" align="center" gap={4}>
                  <IconBox variant="gradient" size="xl">
                    <FaUserPlus fontSize="36px" />
                  </IconBox>
                  <Heading size="lg" color={sc.heading}>
                    {t("signup.title")}
                  </Heading>
                  <Text color={sc.textMuted} textAlign="center">
                    {t("signup.subtitle")}
                  </Text>
                </Flex>

                <VStack w="100%" gap={4}>
                  <Field
                    invalid={!!errors.full_name}
                    errorText={errors.full_name?.message}
                  >
                    <VisionInput
                      {...register("full_name")}
                      placeholder={t("fields.fullName")}
                      type="text"
                      size="lg"
                      variant="glass"
                    />
                  </Field>

                  <Field
                    required
                    invalid={!!errors.email}
                    errorText={errors.email?.message}
                  >
                    <VisionInput
                      {...register("email", {
                        required: "Email is required",
                        pattern: emailPattern,
                      })}
                      placeholder={t("fields.email")}
                      type="email"
                      size="lg"
                      variant="glass"
                      required
                    />
                  </Field>

                  <Field
                    required
                    invalid={!!errors.password}
                    errorText={errors.password?.message}
                  >
                    <InputGroup
                      width="100%"
                      endElement={
                        <ReactIcon
                          icon={show ? FaEyeSlash : FaEye}
                          cursor="pointer"
                          onClick={toggle}
                          aria-label={
                            show ? t("hidePassword") : t("showPassword")
                          }
                          color={sc.textMuted}
                          _hover={{ color: sc.heading }}
                        />
                      }
                    >
                      <VisionInput
                        {...register("password", passwordRules())}
                        type={show ? "text" : "password"}
                        placeholder={t("fields.password")}
                        size="lg"
                        variant="glass"
                        required
                      />
                    </InputGroup>
                  </Field>

                  <Field
                    required
                    invalid={!!errors.confirm_password}
                    errorText={errors.confirm_password?.message}
                  >
                    <InputGroup
                      width="100%"
                      endElement={
                        <ReactIcon
                          icon={show ? FaEyeSlash : FaEye}
                          cursor="pointer"
                          onClick={toggle}
                          aria-label={
                            show ? t("hidePassword") : t("showPassword")
                          }
                          color={sc.textMuted}
                          _hover={{ color: sc.heading }}
                        />
                      }
                    >
                      <VisionInput
                        {...register(
                          "confirm_password",
                          confirmPasswordRules(getValues),
                        )}
                        type={show ? "text" : "password"}
                        placeholder={t("reset.confirmPassword")}
                        size="lg"
                        variant="glass"
                        required
                      />
                    </InputGroup>
                  </Field>

                  <VisionButton
                    type="submit"
                    loading={isSubmitting}
                    size="lg"
                    w="100%"
                    variant="brand"
                  >
                    {t("signup.submit")}
                  </VisionButton>

                  <Text color={sc.textMuted} fontSize="sm">
                    {t("signup.hasAccount")}{" "}
                    <RouterLink
                      to="/login"
                      color="brand.200"
                      fontWeight="semibold"
                    >
                      {t("signup.logIn")}
                    </RouterLink>
                  </Text>
                </VStack>
              </VStack>
            </VisionCard>
          </GradientBorder>
        </VStack>
      </Container>
    </Flex>
  )
}
